import { FileAddOutlined } from '@ant-design/icons'
import type { AutomaticAccessorial, ManualAccessorial } from '@api/useFreight/types'
import { GraviButton, Horizontal, Texto, useLocalStorage } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { DatePicker, Form, InputNumber, message, Popover, Switch, Tooltip } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useEffect, useState } from 'react'

// Form values for rate scheduling
export interface RateFormValues {
  effective_date: dayjs.Dayjs
  effective_to?: dayjs.Dayjs
  rate: number
  distance?: number
}

// Helper function to check if fee type is percentage based
export const isPercentageFeeType = (type: string): boolean => {
  return type?.includes('%') || false
}

// Base props for schedule buttons
export interface BaseScheduleButtonProps {
  onScheduleSuccess?: () => void
  initialRate?: number
}

// Generic Schedule Rate Button Component
export function ScheduleRateButton<T extends ManualAccessorial | AutomaticAccessorial>({
  accessorial,
  onScheduleSuccess,
  initialRate,
  createRate,
  invalidateQueryKey,
  hideEndDate = false,
  isLoading = false,
}: {
  accessorial: T
  onScheduleSuccess?: () => void
  initialRate?: number
  createRate: (data: {
    accessorial_id: string
    effective_from_date: string
    effective_to_date?: string
    rate: number
    distance?: number
  }) => Promise<void>
  invalidateQueryKey: string[]
  hideEndDate?: boolean
  isLoading?: boolean
}) {
  const queryClient = useQueryClient()
  const [form] = Form.useForm<RateFormValues>()
  const [popoverVisible, setPopoverVisible] = useState(false)

  // Use localStorage to persist the showEffectiveTo switch state, but only if not hidden
  const { value: showEffectiveTo, setValue: setShowEffectiveTo } = useLocalStorage(
    'accessorial-show-effective-to',
    false
  )

  // Determine if this is a manual accessorial by checking if it's an instance of ManualAccessorial
  const isManualAccessorial = !('service_rule' in accessorial)

  // When showEffectiveTo changes, we need to update form validation
  useEffect(() => {
    // Reset the effective_to field when the switch is toggled off or when hideEndDate is true
    if (!showEffectiveTo || hideEndDate) {
      form.setFieldValue('effective_to', undefined)
    }

    // This will force the form to revalidate
    form.validateFields(['effective_to']).catch((e) => {
      // We can ignore validation errors here
    })
  }, [showEffectiveTo, form, hideEndDate])

  const isPercentageType = isPercentageFeeType(accessorial.fee_type?.type || '')

  // Get the latest rate
  const getLatestRate = () => {
    if (!accessorial.rates || accessorial.rates.length === 0) return null
    return [...accessorial.rates].sort((a, b) => {
      return new Date(b.effective_from).getTime() - new Date(a.effective_from).getTime()
    })[0]
  }

  const calculateMinimumDate = () => {
    const latestRate = getLatestRate()
    if (!latestRate) return dayjs()

    if (latestRate.effective_to) {
      return dayjs(latestRate.effective_to as string)
    }
    return dayjs(latestRate.effective_from).add(1, 'day')
  }

  const handleScheduleRate = async (values: RateFormValues) => {
    try {
      const effectiveDate = values.effective_date.format('YYYY-MM-DD')
      const effectiveToDate =
        showEffectiveTo && values.effective_to ? values.effective_to.format('YYYY-MM-DD') : undefined

      // Convert percentage to decimal for API if it's a percentage type
      const apiRate = isPercentageType ? Number((values.rate / 100).toFixed(5)) : values.rate

      await createRate({
        accessorial_id: accessorial._id as string,
        effective_from_date: effectiveDate,
        effective_to_date: effectiveToDate,
        rate: apiRate,
      })

      await queryClient.invalidateQueries({ queryKey: invalidateQueryKey })
      onScheduleSuccess?.()
      message.success('Rate scheduled successfully')
      setPopoverVisible(false)
    } catch (error) {
      console.error('Error scheduling rate:', error)
      message.error('Failed to schedule rate')
    } finally {
      setIsLoading(false)
    }
  }

  const renderPopoverContent = () => {
    const latestRate = getLatestRate()
    const minimumAllowedDate = calculateMinimumDate()
    const defaultScheduleDate = minimumAllowedDate

    // Use initialRate if provided, otherwise calculate from latest rate
    const displayRate =
      initialRate !== undefined
        ? initialRate
        : latestRate?.rate
          ? isPercentageType
            ? latestRate.rate * 100
            : latestRate.rate
          : 0

    return (
      <Form
        form={form}
        layout='vertical'
        onFinish={handleScheduleRate}
        initialValues={{
          effective_date: defaultScheduleDate,
          rate: displayRate,
        }}
      >
        <Form.Item
          name='rate'
          label='Rate'
          rules={[
            { required: true, message: 'Please enter rate' },
            {
              validator: (_, value) => {
                if (isPercentageType && (value < 0 || value > 200)) {
                  return Promise.reject('Percentage must be between 0 and 200')
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <InputNumber
            prefix={isPercentageType ? undefined : '$'}
            suffix={isPercentageType ? '%' : undefined}
            precision={isPercentageType ? 3 : 5}
            step={1.0}
            min={0}
            max={isPercentageType ? 200 : undefined}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name='effective_date'
          label='Effective From'
          rules={[{ required: true, message: 'Please select effective date' }]}
        >
          <DatePicker
            format='MM/DD/YYYY'
            style={{ width: '100%' }}
            disabledDate={(current) => current?.isBefore(minimumAllowedDate, 'day')}
          />
        </Form.Item>

        {!hideEndDate && (
          <>
            <Form.Item>
              <Horizontal verticalCenter gap={8}>
                <Switch checked={showEffectiveTo} onChange={setShowEffectiveTo} />
                <Texto>Set end date</Texto>
              </Horizontal>
            </Form.Item>

            {showEffectiveTo && (
              <Form.Item
                name='effective_to'
                label='Effective To'
                rules={[
                  {
                    required: showEffectiveTo,
                    message: 'Please select end date',
                  },
                  {
                    validator: (_, value) => {
                      if (showEffectiveTo && value) {
                        const startDate = form.getFieldValue('effective_date')
                        if (startDate && value.isBefore(startDate)) {
                          return Promise.reject('End date must be after start date')
                        }
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <DatePicker
                  format='MM/DD/YYYY'
                  style={{ width: '100%' }}
                  disabledDate={(current) => {
                    const startDate = form.getFieldValue('effective_date')
                    return (
                      current && (current.isBefore(startDate, 'day') || current.isBefore(minimumAllowedDate, 'day'))
                    )
                  }}
                />
              </Form.Item>
            )}
          </>
        )}

        <Horizontal gap={8} justifyContent='flex-end'>
          <GraviButton appearance='outlined' onClick={() => setPopoverVisible(false)}>
            Cancel
          </GraviButton>
          <GraviButton appearance='solid' theme2 htmlType='submit' loading={isLoading}>
            Schedule
          </GraviButton>
        </Horizontal>
      </Form>
    )
  }

  return (
    <Tooltip title='Schedule New Rate'>
      <Popover
        content={renderPopoverContent()}
        title='Schedule New Rate'
        trigger='click'
        placement='topRight'
        open={popoverVisible}
        onOpenChange={(visible) => {
          if (visible) {
            const latestRate = getLatestRate()
            const defaultScheduleDate = latestRate ? dayjs(latestRate.effective_from) : dayjs()

            // For effective_to, use the day after effective_from if no existing date
            const defaultEffectiveToDate = showEffectiveTo
              ? latestRate?.effective_to
                ? dayjs(latestRate.effective_to as string)
                : dayjs().add(30, 'days')
              : undefined

            const displayRate = latestRate?.rate ? (isPercentageType ? latestRate.rate * 100 : latestRate.rate) : 0

            form.setFieldsValue({
              effective_date: defaultScheduleDate,
              effective_to: defaultEffectiveToDate,
              rate: displayRate,
            })
          }
          setPopoverVisible(visible)
        }}
      >
        <GraviButton
          theme2
          icon={<FileAddOutlined />}
          loading={isLoading}
          buttonText='Schedule New Rate'
          onClick={() => setPopoverVisible(true)}
        />
      </Popover>
    </Tooltip>
  )
}
